import {
  IAttribute,
  IRelationObject,
  IRelationObjectPayload,
  RelationContentType,
  RelationTargetType,
} from "@interfaces/attribute.interface";
import { ITagResponse } from "@interfaces/responses.interface";
import { ICompanyTag, IDealTag, IPeopleTag } from "@interfaces/tag.interface";

/** Render order; also the fallback when a field restricts nothing. */
const ALL_TARGETS_IN_ORDER: RelationTargetType[] = ["deals", "companies", "people"];

/** The order the backend returns relations in — the only one that survives a reload. */
const RELATION_TYPE_ORDER: RelationContentType[] = [
  "companies.company",
  "deals.deal",
  "people.person",
];

const CONTENT_TYPE_TO_TARGET: Record<RelationContentType, RelationTargetType> = {
  "deals.deal": "deals",
  "companies.company": "companies",
  "people.person": "people",
};

/** Backend `relation_content_types` to ordered target keys; unset means all. */
export const resolveAllowedTargets = (
  contentTypes: RelationContentType[] | undefined
): RelationTargetType[] => {
  if (!contentTypes?.length) return [...ALL_TARGETS_IN_ORDER];

  const targets = contentTypes.map((ct) => CONTENT_TYPE_TO_TARGET[ct]);

  return ALL_TARGETS_IN_ORDER.filter((t) => targets.includes(t));
};

type RelationTag = IDealTag | ICompanyTag | IPeopleTag;

/** Wrap a search result into a relation. */
export const toRelationObject = (tag: RelationTag): IRelationObject => ({
  id: tag.id,
  object_type: tag.object_type,
  object: tag,
});

/** Widen to the `<Tag>` prop type; `pending` is meaningless for a relation. */
export const toTagResponse = (relation: IRelationObject): ITagResponse => ({
  id: relation.id,
  object_type: relation.object_type,
  object: relation.object,
  pending: false,
});

/** Group by record type, as the backend does. */
export const sortRelationsForDisplay = (relations: IRelationObject[]): IRelationObject[] =>
  RELATION_TYPE_ORDER.flatMap((contentType) =>
    relations.filter((r) => r.object_type === contentType)
  );

/** Same record — compared by record id, never the relation id. */
const isSameRecord = (a: IRelationObject, b: IRelationObject): boolean =>
  a.object.id === b.object.id;

/** Same set of records, order-independent. */
export const haveSameRelations = (a: IRelationObject[], b: IRelationObject[]): boolean => {
  if (a.length !== b.length) return false;

  const ids = new Set(b.map((o) => o.object.id));

  return a.every((o) => ids.has(o.object.id));
};

/** Optional on the backend contract; defaults to multi. */
export const isSingleSelectRelation = (attribute: Pick<IAttribute, "is_single_select">): boolean =>
  attribute.is_single_select ?? false;

/** To the `{content_type, object_id}` pairs the API accepts on write. */
export const toRelationObjectPayloads = (relations: IRelationObject[]): IRelationObjectPayload[] =>
  relations.map((relation) => ({
    content_type: relation.object_type,
    object_id: relation.object.id,
  }));

/** Drop by record id. */
export const removeRelation = (
  relations: IRelationObject[],
  relation: IRelationObject
): IRelationObject[] => relations.filter((o) => !isSameRecord(o, relation));

/** Toggle by record id: single-select collapses to the pick, multi appends. */
export const toggleRelation = (
  current: IRelationObject[],
  relation: IRelationObject,
  isSingleSelect: boolean
): { relations: IRelationObject[]; added: boolean } => {
  if (current.some((o) => isSameRecord(o, relation))) {
    return { relations: removeRelation(current, relation), added: false };
  }

  return { relations: isSingleSelect ? [relation] : [...current, relation], added: true };
};
